import { CAPABILITY_TAXONOMY } from '@tandemone/types'
import { useQuery } from '@tanstack/react-query'

import { getSupplierCapabilities } from './api'
import type {
  CapabilitiesDerived,
  CapabilitiesFormState
} from './capabilities-context'
import { SupplierCapabilitiesFormProvider } from './capabilities-provider'
import { CapabilitiesToolbar } from './capabilities-toolbar'
import { CapabilityCategorySection } from './capability-category-section'
import { FormSkeleton } from './form-skeleton'
import { NoneButton } from './none-button'
import { NoneSelectedRow } from './none-selected-row'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'
import { useCapabilitiesForm } from './use-capabilities-form'

const NONE_OPTION = {
  label: 'None of the above',
  description: "My business doesn't offer any of the listed capabilities."
}

export function SupplierCapabilitiesForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const { data } = useQuery({
    queryKey: ['supplier-capabilities'],
    queryFn: ({ signal }) => getSupplierCapabilities(signal)
  })

  if (!data) return <FormSkeleton />
  return (
    <SupplierCapabilitiesFormProvider
      initial={data.capabilities}
      onSuccess={onSuccess}
    >
      <FormBody submitLabel={submitLabel} />
    </SupplierCapabilitiesFormProvider>
  )
}

function FormBody({ submitLabel }: { submitLabel: string }) {
  const { state, derived, dispatch, isSubmitting, handleSubmit } =
    useCapabilitiesForm()

  return (
    <form onSubmit={handleSubmit} className='flex w-full flex-col gap-4'>
      {state.noneSelected ? (
        <section className='rounded-2xl border border-border bg-page p-6'>
          <NoneSelectedRow
            option={NONE_OPTION}
            onClear={() => dispatch({ type: 'clearNone' })}
          />
        </section>
      ) : (
        <section className='flex flex-col gap-4 rounded-2xl border border-border bg-page p-6'>
          <CapabilitiesToolbar />
          <div className='flex flex-col gap-3'>
            {CAPABILITY_TAXONOMY.map(category => (
              <CapabilityCategorySection
                key={category.key}
                category={category}
              />
            ))}
          </div>
          <NoneButton
            label="None of the above. My business doesn't offer any of these."
            onClick={() => dispatch({ type: 'selectNone' })}
          />
        </section>
      )}

      {state.submitError && (
        <p className='text-sm text-red-600'>{state.submitError}</p>
      )}

      <StepFooter hint={footerLabel(state, derived)}>
        <PrimaryButton
          type='submit'
          disabled={!derived.isValid || isSubmitting}
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>
    </form>
  )
}

function footerLabel(
  state: CapabilitiesFormState,
  derived: CapabilitiesDerived
): string {
  if (state.noneSelected) return 'None of the above'
  if (derived.selectedCount === 0) return '0 selected'
  if (derived.incompleteCount > 0) {
    return `${derived.selectedCount - derived.incompleteCount} of ${derived.selectedCount} complete`
  }
  return `${derived.selectedCount} selected`
}
